import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowDown } from "lucide-react";
import { CONTACT } from "@/lib/data";

const HERO_IMG =
  "https://images.unsplash.com/photo-1697155406055-2db32d47ca07?crop=entropy&cs=srgb&fm=jpg&q=90&w=2000";

const lines = ["Authentic.", "Elevated.", "Indian Cuisine."];

const wrap = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.5 } },
};
const line = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 1, ease: [0.16, 1, 0.3, 1] } },
};

export default function Hero() {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const scrollToAbout = () => document.querySelector("#about")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="hero" ref={ref} className="relative h-[100svh] w-full overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <motion.img
          src={HERO_IMG}
          alt="Royal Indian feast"
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 3, ease: [0.16, 1, 0.3, 1] }}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0607]/70 via-[#0A0607]/50 to-[#0A0607]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#7A1F3D]/40 via-transparent to-[#7A1F3D]/30" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="mb-6"
        >
          <img src="/logo.png" alt="Palace of Flavor" className="h-40 md:h-64 lg:h-72 w-auto object-contain drop-shadow-[0_4px_30px_rgba(212,175,55,0.35)]" />
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="font-body text-xs md:text-sm tracking-[0.4em] uppercase text-[#D4AF37] mb-6"
        >
          Royal Catering · {CONTACT.area}
        </motion.p>

        <motion.h1 variants={wrap} initial="hidden" animate="show" className="font-display leading-[0.92]">
          {lines.map((t, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                variants={line}
                className={`block text-5xl sm:text-7xl lg:text-8xl xl:text-9xl tracking-tight ${
                  i === 2 ? "gold-gradient-text italic font-medium" : "text-[#FDFBF7] font-light"
                }`}
              >
                {t}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row items-center gap-4"
        >
          <button
            data-testid="hero-book-btn"
            onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
            className="group relative px-8 py-4 bg-[#D4AF37] text-[#0A0607] font-body text-sm tracking-[0.2em] uppercase overflow-hidden"
          >
            <span className="relative z-10">Reserve Your Feast</span>
          </button>
          <button
            data-testid="hero-menu-btn"
            onClick={() => navigate("/menu")}
            className="px-8 py-4 border border-[#FDFBF7]/30 text-[#FDFBF7] font-body text-sm tracking-[0.2em] uppercase hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300"
          >
            Explore the Menu
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={scrollToAbout}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-[#D4AF37]"
        aria-label="Scroll down"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="block">
          <ArrowDown size={22} />
        </motion.span>
      </motion.button>
    </section>
  );
}
